import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { ReminderManager } from "@/components/ReminderManager";
import { useT, useI18n } from "@/i18n";
import { LayoutDashboard, PlusCircle, ListChecks, TrendingUp, User, Users, LogOut, Settings as SettingsIcon, Shield } from "lucide-react";

const playerNav = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/log", label: "Log", icon: PlusCircle, testid: "nav-log" },
  { to: "/app/entries", label: "Entries", icon: ListChecks, testid: "nav-entries" },
  { to: "/app/progress", label: "Progress", icon: TrendingUp, testid: "nav-progress" },
  { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

const coachNav = [
  { to: "/coach", label: "Players", icon: Users, testid: "nav-players" },
  { to: "/coach/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

const adminNav = [
  { to: "/admin", label: "Console", icon: Shield, testid: "nav-admin" },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const { lang, setLang } = useI18n();
  useEffect(() => { if (user?.language && user.language !== lang) setLang(user.language); }, [user?.language]); // eslint-disable-line
  const nav = user?.role === "coach" ? coachNav : user?.role === "admin" ? adminNav : playerNav;
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-100">
      <ReminderManager />
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo light />
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/coach"} data-testid={n.testid}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-brand text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
                  {t(n.label)}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <OfflineIndicator />
            <LanguageToggle dark />
            {user?.role === "player" && (
              <NavLink to="/app/settings" data-testid="nav-settings" className="inline-flex text-slate-300 hover:text-white p-2">
                <SettingsIcon size={18} />
              </NavLink>
            )}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold">{user?.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-brand font-bold">{user?.role}</span>
            </div>
            <button onClick={doLogout} data-testid="logout-button" className="p-2 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition-colors" title="Log out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-28 md:pb-10">
        <Outlet />
      </main>

      {/* Mobile bottom bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-ink/95 backdrop-blur border-t border-white/10 grain">
        <div className="flex items-center justify-around h-16">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/coach"} data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-1 text-[10px] font-semibold ${isActive ? "text-brand" : "text-slate-400"}`}>
              <n.icon size={20} />
              {t(n.label)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
